import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, imageUrl, money, API } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { LogOut, Plus, Trash2, Pencil, Package, Gavel, X, Bell, Wrench, CalendarCheck, Store, Check, Ban, Star, CalendarPlus, Copy, Smartphone, MessageCircle, Send, Megaphone, Tag, Mail, Fish } from "lucide-react";
import { CORAL_TAXONOMY, FISH_TYPES, EQUIPMENT_TYPES, EQUIPMENT_CONDITIONS, subtypeLabel, subtypesFor } from "@/lib/corals";
import { toast } from "sonner";

const EMPTY_P = { category: "coral", name: "", species: "", coral_type: "", coral_subtype: "", fish_type: "", equipment_type: "", condition: "new", pickup_only: false, description: "", price: "", wholesale_price: "", origin: "", stock: 1, image: "", featured: false, notify_subscribers: false, notify_sms: false };
const toLocalInput = (h) => { const d = new Date(Date.now() + h * 3600000); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const EMPTY_A = { category: "coral", name: "", species: "", description: "", image: "", starting_bid: "", bid_increment: 10, ends_at: toLocalInput(48), notify_sms: false };
const EMPTY_S = { name: "", description: "", icon: "wrench", deposit: 50, rate: "", active: true, order: 99 };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [wholesale, setWholesale] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [emailHealth, setEmailHealth] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [modal, setModal] = useState(null);
  const [feedToken, setFeedToken] = useState("");
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [chatReply, setChatReply] = useState("");
  const [subscribers, setSubscribers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistSeen, setWaitlistSeen] = useState(() => localStorage.getItem("er_waitlist_seen") || "");
  const waitlistNew = waitlist.filter((w) => !waitlistSeen || (w.created_at || "") > waitlistSeen).length;
  const markWaitlistSeen = () => {
    const now = new Date().toISOString();
    localStorage.setItem("er_waitlist_seen", now);
    setWaitlistSeen(now);
  };
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", percent: 10 });
  const [announceForm, setAnnounceForm] = useState({ subject: "", message: "", promo_code: "" });
  const [smsBlast, setSmsBlast] = useState("");
  const [smsBusy, setSmsBusy] = useState(false);
  const [waitlistMsg, setWaitlistMsg] = useState("The exotic clownfish are ready! 🐠 Reply or call to claim yours — individually, as a bonded pair, or a ready-to-breed pair. First come, first served.");
  const [waitlistBusy, setWaitlistBusy] = useState(false);
  const waitlistPhones = waitlist.filter((w) => w.phone).length;
  const notifyWaitlist = async () => {
    if (!waitlistMsg.trim()) return toast.error("Write a message first");
    if (!window.confirm(`Email all ${waitlist.length} and text ${waitlistPhones} on the waitlist?`)) return;
    setWaitlistBusy(true);
    try {
      const { data } = await api.post("/admin/clownfish-waitlist/notify", { message: waitlistMsg });
      toast.success(`Notified — ${data.emails_sent} email(s), ${data.texts_sent} text(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not notify waitlist"); }
    finally { setWaitlistBusy(false); }
  };
  const smsSubsCount = subscribers.filter((s) => s.sms_opt_in && s.phone).length;
  const sendSmsBlast = async () => {
    if (!smsBlast.trim()) return toast.error("Write a message first");
    if (!window.confirm(`Send this text to ${smsSubsCount} SMS subscriber(s)?`)) return;
    setSmsBusy(true);
    try {
      const { data } = await api.post("/admin/sms-blast", { message: smsBlast });
      toast.success(`Text sent to ${data.sent_to} subscriber(s)`);
      setSmsBlast("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send SMS"); }
    finally { setSmsBusy(false); }
  };

  const loadP = () => api.get("/products").then((r) => setProducts(r.data));
  const loadA = () => api.get("/auctions").then((r) => setAuctions(r.data));
  const [pubBusy, setPubBusy] = useState(false);
  const publishFrags = async () => {
    if (!window.confirm("This will REMOVE all current auctions, publish an individual auction post for every frag at $30 (ending Sunday midnight), list the buyable corals, and TEXT all SMS subscribers. Continue?")) return;
    setPubBusy(true);
    try {
      const { data } = await api.post("/admin/publish-frag-auctions");
      toast.success(`Published ${data.created} frag auctions · texted ${data.texts_sent} subscriber(s)`);
      loadA();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not publish"); }
    finally { setPubBusy(false); }
  };
  const loadS = () => api.get("/services/all").then((r) => setServices(r.data)).catch(() => {});
  const loadB = () => api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  const loadW = () => api.get("/wholesale").then((r) => setWholesale(r.data)).catch(() => {});
  const loadR = () => api.get("/reviews/all").then((r) => setReviews(r.data)).catch(() => {});
  const loadN = () => api.get("/admin/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  const loadEmailHealth = () => api.get("/admin/email-health").then((r) => setEmailHealth(r.data)).catch(() => {});
  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const { data } = await api.post("/admin/email-test");
      if (data.status === "sent") toast.success(`Test email sent to ${data.recipient}. Check inbox + Junk/Spam.`);
      else if (data.status === "skipped_no_key") toast.error("No email key in THIS environment — emails can't send here.");
      else if (data.status === "no_recipient") toast.error("No recipient email configured.");
      else toast.error(`Send failed: ${data.error || data.status}`);
    } catch (e) { toast.error("Could not run email test"); }
    finally { setTestingEmail(false); loadEmailHealth(); loadN(); }
  };
  const loadChats = () => api.get("/admin/chats").then((r) => setChats(r.data)).catch(() => {});
  const loadSubscribers = () => api.get("/admin/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
  const loadWaitlist = () => api.get("/admin/clownfish-waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
  const loadPromos = () => api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => {});
  const savePromo = async () => {
    if (!promoForm.code.trim()) return toast.error("Enter a code");
    try { await api.post("/admin/promos", { code: promoForm.code, percent: Number(promoForm.percent), active: true }); setPromoForm({ code: "", percent: 10 }); loadPromos(); toast.success("Promo saved"); }
    catch (e) { toast.error("Could not save promo"); }
  };
  const deletePromo = async (code) => { await api.delete(`/admin/promos/${code}`).catch(() => {}); loadPromos(); };
  const sendAnnounce = async () => {
    if (!announceForm.subject.trim() || !announceForm.message.trim()) return toast.error("Subject and message required");
    try { const { data } = await api.post("/admin/announce", announceForm); toast.success(`Sent to ${data.sent_to} subscribers`); setAnnounceForm({ subject: "", message: "", promo_code: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };
  const loadFeedToken = () => api.get("/calendar/token").then((r) => setFeedToken(r.data.token || "")).catch(() => {});
  useEffect(() => { loadP(); loadA(); loadS(); loadB(); loadW(); loadR(); loadN(); loadFeedToken(); loadChats(); loadSubscribers(); loadWaitlist(); loadPromos(); }, []);

  useEffect(() => {
    if (tab !== "chats") return;
    const t = setInterval(loadChats, 5000);
    return () => clearInterval(t);
  }, [tab]);

  useEffect(() => {
    if (!activeChat) return;
    const fresh = chats.find((c) => c.id === activeChat.id);
    if (fresh) setActiveChat(fresh);
  }, [chats]);

  const openChat = async (c) => {
    setActiveChat(c);
    if (c.unread_agent > 0) { await api.post(`/admin/chats/${c.id}/read`).catch(() => {}); loadChats(); }
  };
  const sendReply = async (e) => {
    e.preventDefault();
    const t = chatReply.trim();
    if (!t || !activeChat) return;
    setChatReply("");
    try {
      const { data } = await api.post(`/admin/chats/${activeChat.id}/reply`, { text: t });
      setActiveChat((c) => ({ ...c, messages: [...(c.messages || []), data.message] }));
      loadChats();
    } catch (e) { toast.error("Could not send reply"); }
  };
  const chatUnread = chats.reduce((n, c) => n + (c.unread_agent || 0), 0);

  const feedHttps = feedToken ? `${API}/calendar/bookings.ics?token=${feedToken}` : "";
  const webcalUrl = feedHttps.replace(/^https?:\/\//, "webcal://");
  const copyFeed = () => { navigator.clipboard.writeText(feedHttps); toast.success("Calendar subscription link copied"); };

  const pad2 = (n) => String(n).padStart(2, "0");
  const icsEscape = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
  const downloadIcs = (b) => {
    const ymd = (b.scheduled_date || "").slice(0, 10);
    if (!ymd) return toast.error("This booking has no date");
    const [y, m, d] = ymd.split("-").map(Number);
    const start = `${y}${pad2(m)}${pad2(d)}`;
    const e = new Date(Date.UTC(y, m - 1, d + 1));
    const end = `${e.getUTCFullYear()}${pad2(e.getUTCMonth() + 1)}${pad2(e.getUTCDate())}`;
    const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const desc = [`Customer: ${b.customer_name}`, `Phone: ${b.customer_phone || "n/a"}`, `Email: ${b.customer_email || "n/a"}`, `Deposit paid: ${money(b.deposit_amount)}`, b.notes ? `Notes: ${b.notes}` : ""].filter(Boolean).map(icsEscape).join("\\n");
    const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Exotic Reefing Inc//Bookings//EN", "BEGIN:VEVENT",
      `UID:${b.id}@exoticreefinginc.com`, `DTSTAMP:${stamp}`, `DTSTART;VALUE=DATE:${start}`, `DTEND;VALUE=DATE:${end}`,
      `SUMMARY:${icsEscape(b.service_name + " — " + b.customer_name)}`, `DESCRIPTION:${desc}`];
    if (b.address) lines.push(`LOCATION:${icsEscape(b.address)}`);
    lines.push("STATUS:CONFIRMED", "END:VEVENT", "END:VCALENDAR");
    const blob = new Blob([lines.join("\r\n")], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `booking-${b.customer_name.replace(/\s+/g, "-")}-${ymd}.ics`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const doLogout = () => { logout(); nav("/admin/login"); };

  const saveProduct = async () => {
    const d = modal.data;
    if (!d.name || !d.price) return toast.error("Name and price are required");
    const payload = { ...d, price: parseFloat(d.price), stock: parseInt(d.stock) || 0,
      wholesale_price: d.wholesale_price === "" || d.wholesale_price == null ? null : parseFloat(d.wholesale_price), origin: d.origin || "" };
    try {
      if (modal.editing) await api.put(`/products/${d.id}`, payload); else await api.post("/products", payload);
      toast.success("Saved"); setModal(null); loadP();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const saveAuction = async () => {
    const d = modal.data;
    if (!d.name || !d.starting_bid || !d.ends_at) return toast.error("Name, starting bid and end time required");
    const payload = { category: d.category, name: d.name, species: d.species, description: d.description, image: d.image,
      starting_bid: parseFloat(d.starting_bid), bid_increment: parseFloat(d.bid_increment) || 5, ends_at: new Date(d.ends_at).toISOString(), notify_sms: !!d.notify_sms };
    try {
      if (modal.editing) await api.put(`/auctions/${d.id}`, payload); else await api.post("/auctions", payload);
      toast.success("Saved"); setModal(null); loadA();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const saveService = async () => {
    const d = modal.data;
    if (!d.name) return toast.error("Name is required");
    const payload = { name: d.name, description: d.description, icon: d.icon || "wrench", deposit: parseFloat(d.deposit) || 0, rate: d.rate, active: !!d.active, order: parseInt(d.order) || 0 };
    try {
      if (modal.editing) await api.put(`/services/${d.id}`, payload); else await api.post("/services", payload);
      toast.success("Saved"); setModal(null); loadS();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (type, id, reload) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/${type}/${id}`); toast.success("Deleted"); reload();
  };

  const setWholesaleStatus = async (id, status) => {
    try { await api.put(`/wholesale/${id}/status`, { status }); toast.success(`Marked ${status}`); loadW(); }
    catch (e) { toast.error("Update failed"); }
  };

  const approveReview = async (id) => { try { await api.put(`/reviews/${id}/approve`); toast.success("Review approved"); loadR(); } catch { toast.error("Failed"); } };
  const deleteReview = async (id) => { if (!window.confirm("Delete this review?")) return; await api.delete(`/reviews/${id}`); toast.success("Deleted"); loadR(); };

  const TABS = [
    { k: "products", i: Package, l: "Products" },
    { k: "auctions", i: Gavel, l: "Auctions" },
    { k: "services", i: Wrench, l: "Services" },
    { k: "bookings", i: CalendarCheck, l: "Bookings" },
    { k: "wholesale", i: Store, l: "Wholesale" },
    { k: "reviews", i: Star, l: "Reviews" },
    { k: "chats", i: MessageCircle, l: "Chat" },
    { k: "waitlist", i: Fish, l: "Clownfish Waitlist" },
    { k: "marketing", i: Megaphone, l: "Marketing" },
    { k: "notifications", i: Bell, l: "Notifications" },
  ];

  return (
    <div className="min-h-screen pt-24 pb-20 max-w-[1300px] mx-auto px-5 md:px-10">
      <div className="flex items-center justify-between mb-10">
        <div>
          <span className="mono-label text-cyan">Dashboard</span>
          <h1 className="font-display text-4xl md:text-5xl">Manage business</h1>
          <p className="text-white/40 text-sm mt-1 font-mono">{user?.email}</p>
        </div>
        <button onClick={doLogout} data-testid="logout-btn" className="btn-pill !py-2.5 flex items-center gap-2"><LogOut size={15} /> Logout</button>
      </div>

      <div className="flex gap-3 mb-8 flex-wrap">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => { setTab(t.k); if (t.k === "notifications") { loadN(); loadEmailHealth(); } if (t.k === "bookings") loadB(); if (t.k === "wholesale") loadW(); if (t.k === "reviews") loadR(); if (t.k === "chats") loadChats(); if (t.k === "waitlist") { loadWaitlist(); markWaitlistSeen(); } if (t.k === "marketing") { loadSubscribers(); loadPromos(); } }} data-testid={`tab-${t.k}`}
            className={`mono-label px-5 py-2.5 rounded-full border flex items-center gap-2 ${tab === t.k ? "bg-cyan text-void border-cyan" : "border-white/15 text-white/60"}`}>
            <t.i size={14} /> {t.l}
            {t.k === "chats" && chatUnread > 0 && <span data-testid="chat-unread-badge" className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-toxic text-void text-[10px] font-mono flex items-center justify-center">{chatUnread}</span>}
            {t.k === "waitlist" && waitlistNew > 0 && <span data-testid="waitlist-new-badge" className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-reefpink text-void text-[10px] font-mono flex items-center justify-center">{waitlistNew}</span>}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <>
          <button onClick={() => setModal({ type: "product", data: { ...EMPTY_P }, editing: false })} data-testid="new-product-btn" className="btn-pill btn-solid flex items-center gap-2 mb-6"><Plus size={15} /> New product</button>
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4 border-b border-white/5 hover:bg-white/[0.02]">
                <img src={imageUrl(p.image)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg truncate">{p.name} {p.featured && <span className="text-toxic text-xs">★</span>}</p>
                  <p className="mono-label text-white/40">{p.category} · stock {p.stock}</p>
                </div>
                <span className="font-mono text-cyan">{money(p.price)}</span>
                <button onClick={() => setModal({ type: "product", data: { ...p }, editing: true })} className="p-2 text-white/50 hover:text-cyan"><Pencil size={16} /></button>
                <button onClick={() => del("products", p.id, loadP)} data-testid={`del-product-${p.id}`} className="p-2 text-white/50 hover:text-reefpink"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "auctions" && (
        <>
          <div className="glass rounded-2xl border border-reefpink/30 p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4 justify-between" data-testid="publish-frags-card">
            <div>
              <h3 className="font-display text-lg flex items-center gap-2"><Gavel size={16} className="text-reefpink" /> Go live: tonight's frag auction</h3>
              <p className="text-white/50 text-sm mt-1">Clears old auctions, publishes an <b className="text-white/80">individual post for every frag</b> at <b className="text-white/80">$30</b> (ends Sunday midnight ET), lists the buyable corals, and texts all SMS subscribers.</p>
            </div>
            <button onClick={publishFrags} disabled={pubBusy} data-testid="publish-frags-btn" className="btn-pill btn-pink flex items-center gap-2 shrink-0 disabled:opacity-50">
              <Send size={15} /> {pubBusy ? "Publishing…" : "Publish + broadcast"}
            </button>
          </div>
          <button onClick={() => setModal({ type: "auction", data: { ...EMPTY_A }, editing: false })} data-testid="new-auction-btn" className="btn-pill btn-solid flex items-center gap-2 mb-6"><Plus size={15} /> New auction</button>
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            {auctions.map((a) => (
              <div key={a.id} className="flex items-center gap-4 p-4 border-b border-white/5 hover:bg-white/[0.02]">
                <img src={imageUrl(a.image)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg truncate">{a.name}</p>
                  <p className="mono-label text-white/40">{a.bids?.length || 0} bids · ends {new Date(a.ends_at).toLocaleString()}</p>
                </div>
                <span className="font-mono text-cyan">{money(a.current_bid)}</span>
                <button onClick={() => setModal({ type: "auction", data: { ...a, ends_at: new Date(a.ends_at).toISOString().slice(0, 16) }, editing: true })} className="p-2 text-white/50 hover:text-cyan"><Pencil size={16} /></button>
                <button onClick={() => del("auctions", a.id, loadA)} data-testid={`del-auction-${a.id}`} className="p-2 text-white/50 hover:text-reefpink"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "services" && (
        <>
          <button onClick={() => setModal({ type: "service", data: { ...EMPTY_S }, editing: false })} data-testid="new-service-btn" className="btn-pill btn-solid flex items-center gap-2 mb-6"><Plus size={15} /> New service</button>
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            {services.map((s) => (
              <div key={s.id} className="flex items-center gap-4 p-4 border-b border-white/5 hover:bg-white/[0.02]">
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg truncate">{s.name} {!s.active && <span className="text-white/30 text-xs">(hidden)</span>}</p>
                  <p className="mono-label text-white/40 truncate">{s.rate || "quoted"} · icon: {s.icon}</p>
                </div>
                <span className="font-mono text-cyan">Deposit {money(s.deposit)}</span>
                <button onClick={() => setModal({ type: "service", data: { ...s }, editing: true })} className="p-2 text-white/50 hover:text-cyan"><Pencil size={16} /></button>
                <button onClick={() => del("services", s.id, loadS)} data-testid={`del-service-${s.id}`} className="p-2 text-white/50 hover:text-reefpink"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "bookings" && (
        <div className="space-y-5">
          <div className="glass rounded-2xl border border-cyan/20 p-5" data-testid="calendar-subscribe-banner">
            <div className="flex items-start gap-3">
              <Smartphone className="text-cyan shrink-0 mt-0.5" size={20} />
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg">Sync bookings to your iPhone calendar</p>
                <p className="text-white/50 text-sm mt-1">Subscribe once and every confirmed booking appears automatically. On iPhone: <b className="text-white/70">Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar</b>, then paste this link.</p>
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <input readOnly value={feedHttps} data-testid="calendar-feed-url" onFocus={(e) => e.target.select()}
                    className="er-input flex-1 !py-2 text-xs font-mono text-cyan/90" />
                  <button onClick={copyFeed} data-testid="copy-calendar-url" className="btn-pill !py-2 !px-4 flex items-center justify-center gap-2 whitespace-nowrap"><Copy size={14} /> Copy link</button>
                  <a href={webcalUrl} data-testid="subscribe-calendar-btn" className="btn-pill btn-solid !py-2 !px-4 flex items-center justify-center gap-2 whitespace-nowrap"><CalendarPlus size={14} /> Add to calendar</a>
                </div>
                <p className="text-white/30 text-xs mt-2 font-mono">Keep this link private — it's your personal booking feed.</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="bookings-list">
            {bookings.length === 0 ? (
              <p className="text-white/40 font-mono p-8 text-center">No bookings yet.</p>
            ) : bookings.map((b) => (
              <div key={b.id} className="flex items-center gap-4 p-4 border-b border-white/5">
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg truncate">{b.service_name}</p>
                  <p className="mono-label text-white/40 truncate">{b.customer_name} · {b.scheduled_date} · {b.customer_phone || "no phone"}</p>
                </div>
                <span className="font-mono text-cyan text-sm">{money(b.deposit_amount)}</span>
                <span className={`mono-label px-2 py-1 rounded-full border ${b.status === "confirmed" ? "text-cyan border-cyan/40" : "text-toxic border-toxic/40"}`}>{b.status.replace("_", " ")}</span>
                <button onClick={() => downloadIcs(b)} data-testid={`add-calendar-${b.id}`} className="p-2 text-white/50 hover:text-cyan" title="Add to calendar (.ics)"><CalendarPlus size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "wholesale" && (
        <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="wholesale-list">
          {wholesale.length === 0 ? (
            <p className="text-white/40 font-mono p-8 text-center">No wholesale applications yet.</p>
          ) : wholesale.map((w) => (
            <div key={w.id} className="flex items-center gap-4 p-4 border-b border-white/5">
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg truncate">{w.business_name}</p>
                <p className="mono-label text-white/40 truncate">{w.contact_name} · {w.email} · {w.phone || "no phone"}{w.business_type ? ` · ${w.business_type}` : ""}</p>
              </div>
              <span className={`mono-label px-2 py-1 rounded-full border ${w.status === "approved" ? "text-cyan border-cyan/40" : w.status === "rejected" ? "text-reefpink border-reefpink/40" : "text-toxic border-toxic/40"}`}>{w.status}</span>
              <button onClick={() => setWholesaleStatus(w.id, "approved")} data-testid={`ws-approve-${w.id}`} className="p-2 text-white/50 hover:text-cyan" title="Approve"><Check size={16} /></button>
              <button onClick={() => setWholesaleStatus(w.id, "rejected")} data-testid={`ws-reject-${w.id}`} className="p-2 text-white/50 hover:text-reefpink" title="Reject"><Ban size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {tab === "reviews" && (
        <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="reviews-list">
          {reviews.length === 0 ? (
            <p className="text-white/40 font-mono p-8 text-center">No reviews yet.</p>
          ) : reviews.map((r) => (
            <div key={r.id} className="flex items-start gap-4 p-4 border-b border-white/5">
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg">{r.name} <span className="text-toxic text-sm ml-1">{"★".repeat(r.rating)}</span></p>
                <p className="text-white/60 text-sm mt-1">{r.text}</p>
                <p className="mono-label text-white/30 mt-1">{r.location || "—"}</p>
              </div>
              <span className={`mono-label px-2 py-1 rounded-full border shrink-0 ${r.approved ? "text-cyan border-cyan/40" : "text-toxic border-toxic/40"}`}>{r.approved ? "live" : "pending"}</span>
              {!r.approved && <button onClick={() => approveReview(r.id)} data-testid={`review-approve-${r.id}`} className="p-2 text-white/50 hover:text-cyan shrink-0" title="Approve"><Check size={16} /></button>}
              <button onClick={() => deleteReview(r.id)} data-testid={`review-delete-${r.id}`} className="p-2 text-white/50 hover:text-reefpink shrink-0" title="Delete"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {tab === "chats" && (
        <div className="grid md:grid-cols-[320px_1fr] gap-5" data-testid="chats-panel">
          <div className="rounded-2xl border border-white/10 overflow-hidden max-h-[600px] overflow-y-auto" data-testid="chat-sessions">
            {chats.length === 0 ? (
              <p className="text-white/40 font-mono p-8 text-center text-sm">No chats yet. When a visitor messages you, it appears here (and emails ariana.exoticreefing@icloud.com).</p>
            ) : chats.map((c) => {
              const last = c.messages?.[c.messages.length - 1];
              return (
                <button key={c.id} onClick={() => openChat(c)} data-testid={`chat-session-${c.id}`}
                  className={`w-full text-left p-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors ${activeChat?.id === c.id ? "bg-cyan/10" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-display text-base truncate">{c.visitor_name || "Anonymous"}</p>
                    {c.unread_agent > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-toxic text-void text-[10px] font-mono flex items-center justify-center shrink-0">{c.unread_agent}</span>}
                  </div>
                  <p className="text-white/40 text-xs font-mono truncate">{c.visitor_email || "no email"}</p>
                  {last && <p className="text-white/50 text-xs mt-1 truncate">{last.sender === "agent" ? "You: " : ""}{last.text}</p>}
                </button>
              );
            })}
          </div>
          <div className="rounded-2xl border border-white/10 flex flex-col min-h-[600px]" data-testid="chat-conversation">
            {!activeChat ? (
              <div className="flex-1 flex items-center justify-center text-white/30 font-mono text-sm">Select a conversation</div>
            ) : (
              <>
                <div className="px-5 py-4 border-b border-white/10">
                  <p className="font-display text-lg">{activeChat.visitor_name || "Anonymous"}</p>
                  <p className="text-white/40 text-xs font-mono">{activeChat.visitor_email || "no email"}</p>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[430px]">
                  {(activeChat.messages || []).map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "agent" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 text-sm rounded-2xl ${m.sender === "agent" ? "rounded-tr-sm bg-cyan text-void" : "rounded-tl-sm bg-white/10 text-white/85"}`}>{m.text}</div>
                    </div>
                  ))}
                </div>
                <form onSubmit={sendReply} className="p-3 border-t border-white/10 flex items-center gap-2">
                  <input data-testid="chat-reply-input" className="er-input flex-1 !py-2.5" placeholder="Type your reply…" value={chatReply} onChange={(e) => setChatReply(e.target.value)} />
                  <button data-testid="chat-reply-send" type="submit" className="w-11 h-11 shrink-0 rounded-full bg-cyan text-void flex items-center justify-center hover:scale-105 transition-transform"><Send size={17} /></button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "waitlist" && (
        <div className="space-y-6" data-testid="waitlist-panel">
          <div className="glass rounded-2xl p-6 border border-reefpink/25">
            <h3 className="font-display text-2xl mb-1 flex items-center gap-2"><Fish size={20} className="text-reefpink" /> Clownfish Waitlist <span className="text-white/40 text-lg">({waitlist.length})</span></h3>
            <p className="text-white/50 text-sm mb-5">Everyone who signed up for your exotic clownfish (Nemos) from the home page. Tap Text, Call or Email to reach them when the babies are ready.</p>
            {waitlist.length > 0 && (
              <div className="mb-6 rounded-2xl border border-cyan/25 bg-cyan/[0.04] p-5" data-testid="waitlist-notify-card">
                <h4 className="font-display text-lg mb-1 flex items-center gap-2"><Send size={16} className="text-cyan" /> Notify the whole waitlist at once</h4>
                <p className="text-white/50 text-sm mb-3">Sends this message by <b className="text-white/70">email to all {waitlist.length}</b> and <b className="text-white/70">text to {waitlistPhones}</b> with a phone number. "Reply STOP to opt out" is added to texts automatically.</p>
                <textarea value={waitlistMsg} onChange={(e) => setWaitlistMsg(e.target.value)} rows={3} data-testid="waitlist-notify-message" className="er-input w-full mb-3" />
                <button onClick={notifyWaitlist} disabled={waitlistBusy} data-testid="waitlist-notify-send" className="btn-pill btn-solid flex items-center gap-2 disabled:opacity-50">
                  <Send size={15} /> {waitlistBusy ? "Sending…" : `Notify everyone (${waitlist.length})`}
                </button>
              </div>
            )}
            {waitlist.length === 0 ? (
              <p className="text-white/40 text-sm font-mono" data-testid="waitlist-empty">No signups yet. Share your site — the "Notify me when Nemos drop" form is on the home page.</p>
            ) : (
              <div className="space-y-3" data-testid="waitlist-list">
                {waitlist.map((w) => (
                  <div key={w.id} data-testid={`waitlist-row-${w.id}`} className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-lg">{w.name || "Anonymous"}</span>
                        {w.interest && <span className="mono-label text-[0.6rem] text-reefpink bg-reefpink/10 px-2 py-0.5 rounded-full border border-reefpink/20">{w.interest}</span>}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm font-mono text-white/55 flex-wrap">
                        <span className="truncate">{w.email}</span>
                        {w.phone && <span className="text-cyan/70 flex items-center gap-1"><Smartphone size={12} /> {w.phone}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {w.phone && <a href={`sms:${w.phone}`} data-testid={`waitlist-text-${w.id}`} className="btn-pill !py-2 !px-4 text-xs flex items-center gap-1.5"><MessageCircle size={13} /> Text</a>}
                      {w.phone && <a href={`tel:${w.phone}`} data-testid={`waitlist-call-${w.id}`} className="btn-pill !py-2 !px-4 text-xs flex items-center gap-1.5"><Smartphone size={13} /> Call</a>}
                      <a href={`mailto:${w.email}`} data-testid={`waitlist-email-${w.id}`} className="btn-pill !py-2 !px-4 text-xs flex items-center gap-1.5"><Mail size={13} /> Email</a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "marketing" && (
        <div className="space-y-6" data-testid="marketing-panel">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Promo codes */}
            <div className="glass rounded-2xl p-6 border border-white/10">
              <h3 className="font-display text-2xl mb-1 flex items-center gap-2"><Tag size={18} className="text-cyan" /> Promo codes</h3>
              <p className="text-white/50 text-sm mb-4">Create seasonal codes (e.g. JULY4, BLACKFRIDAY). Customers enter them at checkout for % off.</p>
              <div className="flex gap-2 mb-4">
                <input value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} placeholder="CODE" data-testid="promo-code-input" className="er-input flex-1 uppercase" />
                <input value={promoForm.percent} onChange={(e) => setPromoForm({ ...promoForm, percent: e.target.value })} type="number" min="1" max="100" data-testid="promo-percent-input" className="er-input w-24" />
                <button onClick={savePromo} data-testid="promo-save-btn" className="btn-pill btn-solid whitespace-nowrap">Add</button>
              </div>
              <div className="space-y-2">
                {promos.length === 0 ? <p className="text-white/40 text-sm font-mono">No promo codes yet.</p> : promos.map((p) => (
                  <div key={p.code} className="flex items-center justify-between p-3 rounded-xl border border-white/10" data-testid={`promo-${p.code}`}>
                    <span className="font-mono"><b className="text-cyan">{p.code}</b> · {p.percent}% off {!p.active && <span className="text-white/30">(inactive)</span>}</span>
                    <button onClick={() => deletePromo(p.code)} className="text-white/40 hover:text-reefpink"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Subscribers */}
            <div className="glass rounded-2xl p-6 border border-white/10">
              <h3 className="font-display text-2xl mb-1 flex items-center gap-2"><Mail size={18} className="text-cyan" /> Subscribers <span className="text-white/40 text-lg">({subscribers.length})</span></h3>
              <p className="text-white/50 text-sm mb-4">People signed up for new-arrival & promo emails. New products can auto-notify them. <span className="text-cyan">{smsSubsCount}</span> opted in to SMS.</p>
              <div className="max-h-[240px] overflow-y-auto space-y-1" data-testid="subscribers-list">
                {subscribers.length === 0 ? <p className="text-white/40 text-sm font-mono">No subscribers yet.</p> : subscribers.map((s) => (
                  <div key={s.id} className="text-sm font-mono text-white/60 py-1 border-b border-white/5 flex items-center justify-between gap-2">
                    <span className="truncate">{s.email}</span>
                    {s.sms_opt_in && s.phone && <span className="text-cyan/70 text-[11px] shrink-0 flex items-center gap-1"><Smartphone size={11} /> {s.phone}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Announce (email) */}
          <div className="glass rounded-2xl p-6 border border-cyan/20">
            <h3 className="font-display text-2xl mb-1 flex items-center gap-2"><Megaphone size={18} className="text-cyan" /> Send an email announcement</h3>
            <p className="text-white/50 text-sm mb-4">Email all {subscribers.length} subscribers about a special or holiday sale. Add a promo code to include it in the email.</p>
            <input value={announceForm.subject} onChange={(e) => setAnnounceForm({ ...announceForm, subject: e.target.value })} placeholder="Subject — e.g. 🎆 July 4th Sale: 10% off!" data-testid="announce-subject" className="er-input w-full mb-3" />
            <textarea value={announceForm.message} onChange={(e) => setAnnounceForm({ ...announceForm, message: e.target.value })} placeholder="Your message to subscribers…" rows={4} data-testid="announce-message" className="er-input w-full mb-3" />
            <div className="flex flex-col sm:flex-row gap-3">
              <input value={announceForm.promo_code} onChange={(e) => setAnnounceForm({ ...announceForm, promo_code: e.target.value.toUpperCase() })} placeholder="Promo code to include (optional)" data-testid="announce-promo" className="er-input flex-1 uppercase" />
              <button onClick={sendAnnounce} data-testid="announce-send" className="btn-pill btn-solid whitespace-nowrap flex items-center justify-center gap-2"><Send size={15} /> Send to {subscribers.length}</button>
            </div>
          </div>

          {/* SMS blast */}
          <div className="glass rounded-2xl p-6 border border-toxic/20" data-testid="sms-blast-card">
            <h3 className="font-display text-2xl mb-1 flex items-center gap-2"><Smartphone size={18} className="text-toxic" /> Send an SMS blast</h3>
            <p className="text-white/50 text-sm mb-4">Text the <span className="text-cyan">{smsSubsCount}</span> subscriber(s) who opted in to SMS — perfect for a live auction or a fresh coral/fish drop. "Reply STOP to opt out" is added automatically.</p>
            <textarea value={smsBlast} onChange={(e) => setSmsBlast(e.target.value)} rows={3} maxLength={280}
              placeholder="e.g. LIVE auction now on a WYSIWYG Torch colony! Bid: exoticreefinginc.com/auctions"
              data-testid="sms-blast-message" className="er-input w-full mb-2" />
            <div className="flex items-center justify-between gap-3">
              <span className="text-white/30 text-xs font-mono">{smsBlast.length}/280</span>
              <button onClick={sendSmsBlast} disabled={smsBusy || smsSubsCount === 0} data-testid="sms-blast-send"
                className="btn-pill btn-solid whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-40">
                <Send size={15} /> {smsBusy ? "Sending…" : `Text ${smsSubsCount}`}
              </button>
            </div>
            {smsSubsCount === 0 && <p className="text-white/40 text-xs mt-2 font-mono">No SMS subscribers yet — customers opt in with their mobile number in the newsletter signup.</p>}
          </div>
        </div>
      )}

      {tab === "notifications" && (
        <>
          <div className="glass rounded-2xl p-5 mb-6 border border-cyan/20" data-testid="email-health-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-display text-2xl flex items-center gap-2"><Mail size={18} className="text-cyan" /> Email health</h3>
                <p className="text-white/50 text-sm mt-1 max-w-xl">Confirms whether email delivery is working in <b className="text-white/80">this exact environment</b>. Open this on your live site to check production.</p>
              </div>
              <button onClick={sendTestEmail} disabled={testingEmail} data-testid="send-test-email-btn" className="btn-pill btn-solid flex items-center gap-2 disabled:opacity-50">
                <Send size={14} /> {testingEmail ? "Sending…" : "Send test email"}
              </button>
            </div>
            {emailHealth && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="email-health-details">
                <div className={`rounded-xl border p-3 ${emailHealth.env.email_key_present ? "border-cyan/30" : "border-reefpink/40"}`}>
                  <p className="mono-label text-white/40">Email key in this environment</p>
                  <p className={`font-mono text-lg ${emailHealth.env.email_key_present ? "text-cyan" : "text-reefpink"}`} data-testid="email-key-status">
                    {emailHealth.env.email_key_present ? "✓ Present" : "✕ MISSING"}
                  </p>
                  {!emailHealth.env.email_key_present && <p className="text-reefpink/80 text-xs mt-1">Emails cannot be sent here. Set EMERGENT_EMAIL_KEY in this environment's variables and redeploy.</p>}
                </div>
                <div className="rounded-xl border border-white/10 p-3">
                  <p className="mono-label text-white/40">Chat alerts go to</p>
                  <p className="font-mono text-white/80 text-sm truncate" data-testid="chat-recipient">{emailHealth.env.effective_chat_recipient || "— not set —"}</p>
                  <p className="mono-label text-white/40 mt-2">Reply-by-email {emailHealth.env.inbound_reply_configured ? <span className="text-cyan">enabled</span> : <span className="text-toxic">off</span>}</p>
                </div>
                {emailHealth.last_chat_alert && (
                  <div className="rounded-xl border border-white/10 p-3 sm:col-span-2">
                    <p className="mono-label text-white/40">Most recent chat email alert</p>
                    <p className="text-sm text-white/80 mt-0.5">
                      <span className={`font-mono ${emailHealth.last_chat_alert.status === "sent" ? "text-cyan" : emailHealth.last_chat_alert.status?.startsWith("skipped") ? "text-toxic" : "text-reefpink"}`}>{emailHealth.last_chat_alert.status}</span>
                      <span className="text-white/40"> · {new Date(emailHealth.last_chat_alert.created_at).toLocaleString()}</span>
                    </p>
                    {emailHealth.last_chat_alert.error && <p className="text-reefpink/80 text-xs mt-1 font-mono break-words">{emailHealth.last_chat_alert.error}</p>}
                  </div>
                )}
              </div>
            )}
            <p className="text-white/30 text-xs mt-3">If a test shows <span className="text-cyan">sent</span> but you never receive it, check your <b className="text-white/60">Junk/Spam</b> folder and mark it "Not Junk" — iCloud can filter new senders.</p>
          </div>
          <div className="glass rounded-xl p-4 mb-6 border border-cyan/20 text-sm text-white/60">
            <p className="mb-1"><span className="text-cyan font-mono">Owner alerts</span> go to <b className="text-white">exoticreefinginc@icloud.com</b> and SMS <b className="text-white">954-868-0608</b> for new bids, auction endings, paid orders and service bookings.</p>
            <p className="text-white/40 text-xs">Rows marked <span className="text-toxic">skipped_no_key</span> / <span className="text-toxic">skipped_no_twilio</span> mean email/SMS delivery isn't enabled yet — alerts are logged here until then.</p>
          </div>
          <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="notifications-list">
            {notifs.length === 0 ? (
              <p className="text-white/40 font-mono p-8 text-center">No notifications yet.</p>
            ) : notifs.map((n, i) => (
              <div key={i} className="flex items-center gap-4 p-4 border-b border-white/5">
                <span className={`mono-label px-2 py-1 rounded-full border ${n.channel === "email" ? "text-cyan border-cyan/30" : "text-reefpink border-reefpink/30"}`}>{n.channel}</span>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-white/80 text-sm">{n.subject}</p>
                  <p className="mono-label text-white/40">{n.to}</p>
                </div>
                <span className={`mono-label ${n.status === "sent" ? "text-cyan" : n.status?.startsWith("skipped") ? "text-toxic" : "text-reefpink"}`}>{n.status}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {modal && (
        <div className="fixed inset-0 z-[100] bg-void/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-5">
          <div className="glass rounded-2xl p-8 w-full max-w-lg my-10 border border-white/10" data-testid="admin-modal">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-2xl">{modal.editing ? "Edit" : "New"} {modal.type}</h3>
              <button onClick={() => setModal(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>

            {(modal.type === "product" || modal.type === "auction") && (
              <Field label="Category">
                <select value={modal.data.category} onChange={(e) => setModal({ ...modal, data: { ...modal.data, category: e.target.value } })} className="er-input" data-testid="field-category">
                  <option value="coral">Coral</option><option value="fish">Fish</option>
                  {modal.type === "product" && <option value="equipment">Tanks & Equipment</option>}
                </select>
              </Field>
            )}
            <Field label="Name"><input className="er-input" data-testid="field-name" value={modal.data.name} onChange={(e) => setModal({ ...modal, data: { ...modal.data, name: e.target.value } })} /></Field>
            {((modal.type === "product" && modal.data.category !== "equipment") || modal.type === "auction") && (
              <Field label="Species"><input className="er-input" value={modal.data.species} onChange={(e) => setModal({ ...modal, data: { ...modal.data, species: e.target.value } })} /></Field>
            )}
            <Field label="Description"><textarea rows={3} className="er-input" value={modal.data.description} onChange={(e) => setModal({ ...modal, data: { ...modal.data, description: e.target.value } })} /></Field>
            {(modal.type === "product" || modal.type === "auction") && (
              <Field label="Image"><ImageUpload value={modal.data.image} onChange={(v) => setModal({ ...modal, data: { ...modal.data, image: v } })} testid="field-image" /></Field>
            )}

            {modal.type === "product" && (
              <>
                {modal.data.category === "coral" && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Coral type">
                      <select className="er-input" data-testid="field-coral-type" value={modal.data.coral_type || ""}
                        onChange={(e) => setModal({ ...modal, data: { ...modal.data, coral_type: e.target.value, coral_subtype: "" } })}>
                        <option value="">—</option>
                        {CORAL_TAXONOMY.map((c) => <option key={c.type} value={c.type}>{c.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Sub-type">
                      <select className="er-input" data-testid="field-coral-subtype" value={modal.data.coral_subtype || ""}
                        disabled={subtypesFor(modal.data.coral_type).length === 0}
                        onChange={(e) => setModal({ ...modal, data: { ...modal.data, coral_subtype: e.target.value } })}>
                        <option value="">{subtypesFor(modal.data.coral_type).length === 0 ? "— (n/a)" : "—"}</option>
                        {subtypesFor(modal.data.coral_type).map((s) => <option key={s} value={s}>{subtypeLabel(s)}</option>)}
                      </select>
                    </Field>
                  </div>
                )}
                {modal.data.category === "fish" && (
                  <Field label="Fish category">
                    <select className="er-input" data-testid="field-fish-type" value={modal.data.fish_type || ""}
                      onChange={(e) => setModal({ ...modal, data: { ...modal.data, fish_type: e.target.value } })}>
                      <option value="">—</option>
                      {FISH_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                )}
                {modal.data.category === "equipment" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Condition">
                        <select className="er-input" data-testid="field-condition" value={modal.data.condition || "new"}
                          onChange={(e) => setModal({ ...modal, data: { ...modal.data, condition: e.target.value } })}>
                          {EQUIPMENT_CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Equipment type">
                        <input className="er-input" data-testid="field-equipment-type" list="equipment-types" placeholder="Pick or type a custom type"
                          value={modal.data.equipment_type || ""} onChange={(e) => setModal({ ...modal, data: { ...modal.data, equipment_type: e.target.value } })} />
                        <datalist id="equipment-types">{EQUIPMENT_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
                      </Field>
                    </div>
                    <label className="flex items-center gap-2 mb-3 text-sm text-white/70">
                      <input type="checkbox" data-testid="field-pickup-only" checked={!!modal.data.pickup_only} onChange={(e) => setModal({ ...modal, data: { ...modal.data, pickup_only: e.target.checked } })} /> Local pickup only (not shippable)
                    </label>
                  </>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Price (USD)"><input type="number" className="er-input" data-testid="field-price" value={modal.data.price} onChange={(e) => setModal({ ...modal, data: { ...modal.data, price: e.target.value } })} /></Field>
                  <Field label="Wholesale price (USD)"><input type="number" className="er-input" data-testid="field-wholesale-price" placeholder="optional" value={modal.data.wholesale_price ?? ""} onChange={(e) => setModal({ ...modal, data: { ...modal.data, wholesale_price: e.target.value } })} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Stock"><input type="number" className="er-input" value={modal.data.stock} onChange={(e) => setModal({ ...modal, data: { ...modal.data, stock: e.target.value } })} /></Field>
                  {modal.data.category !== "equipment" && (
                    <Field label="Import origin">
                      <select className="er-input" data-testid="field-origin" value={modal.data.origin || ""} onChange={(e) => setModal({ ...modal, data: { ...modal.data, origin: e.target.value } })}>
                        <option value="">—</option><option value="Australia">Australia</option><option value="Indonesia">Indonesia</option>
                      </select>
                    </Field>
                  )}
                </div>
                <label className="flex items-center gap-2 mb-3 text-sm text-white/70">
                  <input type="checkbox" checked={modal.data.featured} onChange={(e) => setModal({ ...modal, data: { ...modal.data, featured: e.target.checked } })} /> Feature on homepage
                </label>
                <label className="flex items-center gap-2 mb-6 text-sm text-white/70">
                  <input type="checkbox" data-testid="notify-subscribers-check" checked={!!modal.data.notify_subscribers} onChange={(e) => setModal({ ...modal, data: { ...modal.data, notify_subscribers: e.target.checked } })} /> Email subscribers about this new arrival
                </label>
                <label className="flex items-center gap-2 mb-6 text-sm text-white/70">
                  <input type="checkbox" data-testid="notify-sms-check" checked={!!modal.data.notify_sms} onChange={(e) => setModal({ ...modal, data: { ...modal.data, notify_sms: e.target.checked } })} /> Text SMS subscribers about this new arrival
                </label>
                <button onClick={saveProduct} data-testid="save-product-btn" className="btn-pill btn-solid w-full">Save product</button>
              </>
            )}

            {modal.type === "auction" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Starting bid (USD)"><input type="number" className="er-input" data-testid="field-starting-bid" value={modal.data.starting_bid} onChange={(e) => setModal({ ...modal, data: { ...modal.data, starting_bid: e.target.value } })} /></Field>
                  <Field label="Bid increment"><input type="number" className="er-input" value={modal.data.bid_increment} onChange={(e) => setModal({ ...modal, data: { ...modal.data, bid_increment: e.target.value } })} /></Field>
                </div>
                <Field label="Ends at"><input type="datetime-local" className="er-input" data-testid="field-ends-at" value={modal.data.ends_at} onChange={(e) => setModal({ ...modal, data: { ...modal.data, ends_at: e.target.value } })} /></Field>
                {!modal.editing && (
                  <label className="flex items-center gap-2 mb-6 text-sm text-white/70">
                    <input type="checkbox" data-testid="auction-notify-sms-check" checked={!!modal.data.notify_sms} onChange={(e) => setModal({ ...modal, data: { ...modal.data, notify_sms: e.target.checked } })} /> Text SMS subscribers that this auction is live
                  </label>
                )}
                <button onClick={saveAuction} data-testid="save-auction-btn" className="btn-pill btn-solid w-full">Save auction</button>
              </>
            )}

            {modal.type === "service" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Deposit (USD)"><input type="number" className="er-input" data-testid="field-deposit" value={modal.data.deposit} onChange={(e) => setModal({ ...modal, data: { ...modal.data, deposit: e.target.value } })} /></Field>
                  <Field label="Rate label"><input className="er-input" placeholder="e.g. From $80/visit" value={modal.data.rate} onChange={(e) => setModal({ ...modal, data: { ...modal.data, rate: e.target.value } })} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Icon (lucide name)"><input className="er-input" placeholder="wrench" value={modal.data.icon} onChange={(e) => setModal({ ...modal, data: { ...modal.data, icon: e.target.value } })} /></Field>
                  <Field label="Sort order"><input type="number" className="er-input" value={modal.data.order} onChange={(e) => setModal({ ...modal, data: { ...modal.data, order: e.target.value } })} /></Field>
                </div>
                <label className="flex items-center gap-2 mb-6 text-sm text-white/70">
                  <input type="checkbox" checked={modal.data.active} onChange={(e) => setModal({ ...modal, data: { ...modal.data, active: e.target.checked } })} /> Show on website
                </label>
                <button onClick={saveService} data-testid="save-service-btn" className="btn-pill btn-solid w-full">Save service</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="mono-label text-white/40 block mb-2">{label}</label>
      {children}
    </div>
  );
}
